import React, { useState } from 'react';
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { closeDialog } from '../../redux/slices/dialog';
import { useAppDispatch } from '../../redux';
import moment, { Moment } from 'moment';
import { TextField } from '../textField';
import { AccessPermission, useShareDocumentMutation } from '../../redux/services';
import copy from 'fast-copy';
import { closeBackdrop, openBackdrop } from '../../redux/slices/backdrop';
import { enqueueSnackbar } from 'notistack';
import { DateTimePicker } from '@mui/x-date-pickers';
import { DATE_TIME_FORMAT } from '../dateTimePicker';

const EmailChipInput = ({ hasError, emails, setEmails }: { hasError: boolean; emails: string[]; setEmails: React.Dispatch<React.SetStateAction<string[]>> }) => {
  const [inputValue, setInputValue] = useState('');

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleInputChange = (event: React.SyntheticEvent<Element, Event>, value: string, reason: AutocompleteInputChangeReason) => {
    if (reason === 'removeOption') return;
    if (value.at(-1) === ",") {
      const inputValue = value.substring(0, value.length - 1) || '';
      const isValidEmail = emailRegex.test(inputValue.trim());

      if (isValidEmail && !emails.includes(inputValue)) {
        setEmails(prev => [...prev, inputValue]);
        // Clear input by setting inputValue to empty string
        setInputValue('');
      }
    } else {
      // Update input value if not ending with comma
      setInputValue(value);
    }
  };

  const handleDelete = (emailToRemove: string) => {
    setEmails(prev => prev.filter(email => email !== emailToRemove));
  };

  return (
    <Autocomplete
      multiple
      freeSolo
      options={[]}
      value={emails}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      renderTags={(value, getTagProps) =>
        value.map((email, index) => (
          <Chip
            variant="outlined"
            label={email}
            {...getTagProps({ index })}
            onDelete={() => handleDelete(email)}
            deleteIcon={<Close />}
            color="primary"
          />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          required
          label={"Email"}
          variant="outlined"
          placeholder="Enter emails (comma-separated) or press Enter to validate"
          error={hasError}
          helperText={hasError ? "Press Enter at least one valid email" : ""}
          inputProps={{
            ...params.inputProps,
            autoComplete: "email",
            type: "email"  // Changes keyboard on mobile to show @ symbol
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
            if (event.type === 'keydown' && (event as React.KeyboardEvent).key === 'Enter') {
              const inputValue = (event.target as HTMLInputElement)?.value;
              const isValidEmail = emailRegex.test(inputValue);

              if (isValidEmail && !emails.includes(inputValue)) {
                setEmails(prev => [...prev, inputValue]);
                // Clear input by setting inputValue to empty string
                setInputValue('');
                // Prevent default Enter key behavior
                event.preventDefault();
                return;
              }
            }
          }}
        />
      )}
    />
  );
};

const DocumentShare = ({ title, documentId }: { title: string; documentId: string }) => {
  const dispatch = useAppDispatch();

  const [shareDocument] = useShareDocumentMutation();

  const [emails, setEmails] = useState<string[]>([]);
  const [accessPermission, setAccessPermission] = React.useState<AccessPermission>('view');
  const [date, setDate] = useState(moment().add(10, 'days'));
  const [message, setMessage] = useState("");

  const [errors, setErrors] = useState({ email: false, date: false, message: false, description: false });

  const validate = () => {
    let isValid = true;

    let err = copy(errors);

    if (emails.length <= 0) {
      isValid = false;
      err.email = true;
    } else {
      err.email = false;
    }

    if (message.trim().length <= 0) {
      isValid = false;
      err.message = true;
    } else {
      err.message = false;
    }

    if (!date) {
      isValid = false;
      err.date = true;
    } else {
      err.date = false;
    }

    setErrors((prev) => ({ ...prev, ...err }))

    return isValid;
  }

  const onShareDocument = async () => {
    if (validate()) {
      dispatch(openBackdrop(`Sharing Document for ${title}...`))
      await shareDocument({
        documentId,
        payload: {
          emails,
          access_permission: accessPermission,
          message,
          expiration_time: date,
          description: ""
        }
      })
        .unwrap()
        .then((res) => {
          enqueueSnackbar("Document shared successfully", { variant: "success" });
        })
        .catch((err) => {
          console.error("Document failed to share: ", JSON.stringify(err))
          enqueueSnackbar(err?.data?.title ?? err?.data ?? "Unable to share the document", { variant: "error" });
        })
        .finally(() => {
          dispatch(closeBackdrop());
          dispatch(closeDialog());
        })
    }
  };

  const giveMeButtonName = (cdData: any) => {
    if (cdData.isError) {
      return `Retry Share`
    }

    return `Share`;
  };

  return (
    <>
      <DialogTitle>
        <Typography
          variant="h6"
          color="textPrimary"
          sx={{
            width: "100%",
            fontFamily: 'htrts_medium',
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          }}
        >
          Share "{title}" <img style={{ marginLeft: "8px" }} src={'/beta.gif'} alt='In Beta' />
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack gap={3} sx={{ mt: 1 }}>
          <EmailChipInput hasError={errors.email} emails={emails} setEmails={setEmails} />

          <FormControl>
            <FormLabel>Select the access permission </FormLabel>
            <RadioGroup value={accessPermission} onChange={(e) => setAccessPermission(e.target.value as AccessPermission)} row>
              <FormControlLabel value="view" control={<Radio />} label="View" />
              <FormControlLabel value="download" control={<Radio />} label="Download" />
            </RadioGroup>
          </FormControl>

          <DateTimePicker
            value={date}
            onChange={(current_date) => {
              setDate(current_date as Moment);
            }}
            label="Expiration Date *"
            format={DATE_TIME_FORMAT}
            minDate={moment().add(1, 'days')}
            formatDensity="spacious"
            sx={{ width: "100%" }}
            slotProps={{
              textField: {
                error: errors.date,
                helperText: errors.date ? "Please enter a valid date" : ""
              }
            }}
          />
          <TextField
            variant="outlined"
            size="small"
            multiline
            rows={3}
            label={"Message"}
            required
            placeholder={"Please enter the message to share in the mail"}
            onChange={(e) => setMessage(e.target.value)}
            value={message}
            error={errors.message}
            helperText={errors.message ? "Please fill the message" : ""}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center !important', mb: 2 }}>
        <Stack
          sx={{
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            flexDirection: { xs: "column", md: "row" }
          }}>
          <Button
            variant="outlined"
            onClick={() => dispatch(closeDialog())}
          >
            Cancel
          </Button>

          <LoadingButton
            variant="contained"
            onClick={onShareDocument}
          // loading={createDocumentData.isLoading || connectingSocket}
          >
            {giveMeButtonName({})}
          </LoadingButton>
        </Stack>
      </DialogActions>
    </>
  )
}

export { DocumentShare };